import React, { useEffect, useMemo, useRef, useState } from 'react';

type EcosystemState = {
  timeOfDay?: string;
  weather?: string;
  karma?: number;
  activeEvents?: Array<{ id?: string; type?: string; faction?: string; intensity?: number }>;
  factionInfluences?: Record<string, number>;
};

type CompanionStatus = Array<{ id?: string; name?: string; archetype?: string; mood?: string } | any>;

interface MapEntity {
  id: string;
  label: string;
  type: 'world' | 'event' | 'companion' | 'faction';
  faction?: string;
  intensity?: number;
}

const BASE_URL =
  (import.meta.env.VITE_AUTONOMOUS_BACKEND_URL as string | undefined)?.replace(/\/$/, '') ??
  'http://localhost:3001';

const fetchJson = async <T,>(path: string): Promise<T | null> => {
  try {
    const response = await fetch(`${BASE_URL}${path}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return (await response.json()) as T;
  } catch (error) {
    console.warn(`WorldMapCanvas failed to fetch ${path}:`, error);
    return null;
  }
};

const hashPosition = (key: string, width: number, height: number) => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash |= 0;
  }

  const margin = 40;
  const x = Math.abs(hash) % (width - margin * 2) + margin;
  const y = Math.abs(hash >> 8) % (height - margin * 2) + margin;
  return { x, y };
};

const deriveEntities = (
  ecosystem: EcosystemState | null,
  companions: CompanionStatus,
  worldEvents: any[]
): MapEntity[] => {
  const entities: MapEntity[] = [];

  entities.push({
    id: 'world-core',
    label: `${ecosystem?.weather ?? 'unknown'} · karma ${ecosystem?.karma ?? 0}`,
    type: 'world'
  });

  (ecosystem?.factionInfluences ? Object.entries(ecosystem.factionInfluences) : []).forEach(
    ([faction, influence]) => {
      entities.push({
        id: `faction-${faction}`,
        label: `${faction} ${(influence * 100).toFixed(0)}%`,
        type: 'faction',
        faction,
        intensity: influence
      });
    }
  );

  (worldEvents ?? ecosystem?.activeEvents ?? []).forEach((event, index) => {
    const label = event?.type ?? 'event';
    entities.push({
      id: `event-${event?.id ?? label}-${index}`,
      label,
      type: 'event',
      faction: event?.faction,
      intensity: event?.intensity ?? 0.4
    });
  });

  companions?.forEach((companion: any, index: number) => {
    const identifier = companion?.id ?? companion?.name ?? `companion-${index}`;
    entities.push({
      id: `companion-${identifier}`,
      label: companion?.name ?? `Companion ${index + 1}`,
      type: 'companion',
      faction: companion?.archetype,
      intensity: companion?.mood === 'aggressive' ? 0.9 : 0.5
    });
  });

  return entities;
};

export const WorldMapCanvas: React.FC<{ className?: string }> = ({ className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<any>(null);

  const [ecosystem, setEcosystem] = useState<EcosystemState | null>(null);
  const [companions, setCompanions] = useState<CompanionStatus>([]);
  const [worldEvents, setWorldEvents] = useState<any[]>([]);
  const [statusMessage, setStatusMessage] = useState<string>('Loading world map...');

  const entities = useMemo(
    () => deriveEntities(ecosystem, companions, worldEvents),
    [ecosystem, companions, worldEvents]
  );

  useEffect(() => {
    let game: any;
    let destroyed = false;

    (async () => {
      const phaserModule = await import('phaser');
      const Phaser: any = (phaserModule as any).default ?? phaserModule;
      if (!containerRef.current || destroyed) return;

      const width = containerRef.current.clientWidth || 640;
      const height = Math.max(containerRef.current.clientHeight, 360) || 360;

      const SceneBase = Phaser.Scene;

      class MapScene extends SceneBase {
        entitySprites: Map<string, { marker: any; label: any }>;

        constructor() {
          super('WorldMapScene');
          this.entitySprites = new Map();
        }

        create() {
          this.drawGrid();
          (this as any).external = {
            upsertEntities: this.upsertEntities.bind(this)
          };
        }

        drawGrid() {
          const graphics = this.add.graphics({ lineStyle: { width: 1, color: 0x1f2937, alpha: 0.6 } });
          const step = 64;
          for (let x = step; x < width; x += step) {
            graphics.lineStyle(1, 0x1f2937, 0.5);
            graphics.lineBetween(x, 0, x, height);
          }
          for (let y = step; y < height; y += step) {
            graphics.lineStyle(1, 0x1f2937, 0.5);
            graphics.lineBetween(0, y, width, y);
          }

          graphics.lineStyle(2, 0x38bdf8, 0.6);
          graphics.strokeRect(12, 12, width - 24, height - 24);
        }

        upsertEntities(entityList: MapEntity[]) {
          const activeIds = new Set<string>();

          entityList.forEach((entity) => {
            activeIds.add(entity.id);
            const existing = this.entitySprites.get(entity.id);
            const { x, y } = hashPosition(
              `${entity.id}-${entity.label}`,
              width,
              height
            );

            const color = this.colorFor(entity.type, entity.faction);
            const intensity = entity.intensity ?? 0.5;

            if (existing) {
              existing.marker.setPosition(x, y);
              existing.marker.setFillStyle(color, 0.35 + intensity * 0.5);
              existing.label.setPosition(x + 12, y - 12);
              existing.label.setText(entity.label);
            } else {
              const marker = this.add.circle(x, y, 10 + intensity * 12, color, 0.35 + intensity * 0.5);
              marker.setStrokeStyle(1.5, color, 0.9);
              const label = this.add.text(x + 12, y - 12, entity.label, {
                color: '#e0f2fe',
                fontSize: '12px'
              });
              label.setShadow(0, 0, '#020617', 4, true, true);
              this.entitySprites.set(entity.id, { marker, label });
            }
          });

          // Remove stale markers
          this.entitySprites.forEach((sprite, id) => {
            if (!activeIds.has(id)) {
              sprite.marker.destroy();
              sprite.label.destroy();
              this.entitySprites.delete(id);
            }
          });
        }

        colorFor(type: MapEntity['type'], faction?: string) {
          switch (type) {
            case 'world':
              return 0x38bdf8;
            case 'event':
              return faction === 'underground' ? 0xf97316 : faction === 'corporate' ? 0x8b5cf6 : 0x14b8a6;
            case 'companion':
              return 0xfbbf24;
            case 'faction':
              return 0x34d399;
            default:
              return 0x60a5fa;
          }
        }
      }

      game = new Phaser.Game({
        type: Phaser.AUTO,
        width,
        height,
        backgroundColor: '#030712',
        parent: containerRef.current,
        scene: [MapScene],
        scale: {
          mode: Phaser.Scale.FIT,
          autoCenter: Phaser.Scale.CENTER_BOTH
        },
        callbacks: {
          postBoot: (gameInstance: any) => {
            const scene = gameInstance.scene.getScene('WorldMapScene') as any;
            sceneRef.current = scene;
          }
        }
      });
    })();

    return () => {
      destroyed = true;
      if (game) {
        game.destroy(true);
      }
      sceneRef.current = null;
    };
  }, []);

  useEffect(() => {
    let active = true;

    (async () => {
      const [eco, comp, world] = await Promise.all([
        fetchJson<EcosystemState>('/ecosystem/status'),
        fetchJson<CompanionStatus>('/companions/status'),
        fetchJson<any>('/world/status')
      ]);

      if (!active) return;
      setEcosystem(eco ?? null);
      setCompanions((comp as CompanionStatus) ?? []);
      setWorldEvents(world?.activeEvents ?? []);
      setStatusMessage('');
    })();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const ws = new WebSocket(BASE_URL.replace(/^http/, 'ws'));

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'autonomous_update') {
          setEcosystem(payload.ecosystem?.worldState ?? payload.ecosystem ?? null);
          setCompanions(payload.companions ?? []);
          setWorldEvents(payload.world?.newEvents ?? payload.world?.events ?? []);
        }
        if (payload.type === 'world_event') {
          setWorldEvents((prev) => [payload.event, ...(prev ?? [])].slice(0, 10));
        }
      } catch (error) {
        console.warn('World map websocket parse error', error);
      }
    };

    ws.onerror = (error) => {
      console.warn('World map websocket error', error);
    };

    return () => {
      ws.close();
    };
  }, []);

  useEffect(() => {
    const scene = sceneRef.current;
    if (scene?.external?.upsertEntities) {
      scene.external.upsertEntities(entities);
    }
  }, [entities]);

  const activeEvents = useMemo(() => (ecosystem?.activeEvents ?? worldEvents ?? []).slice(0, 5), [ecosystem, worldEvents]);

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div
        ref={containerRef}
        style={{
          width: '100%',
          minHeight: '360px',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          background: '#030712'
        }}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#a5b4fc', fontSize: '12px' }}>
          <span>Time of Day: {ecosystem?.timeOfDay ?? '—'}</span>
          <span>Weather: {ecosystem?.weather ?? '—'}</span>
          <span>Karma: {ecosystem?.karma ?? 0}</span>
        </div>
        <div style={{ fontSize: '12px', color: '#cbd5f5' }}>Active Events</div>
        {activeEvents.length === 0 && (
          <div style={{ fontSize: '12px', color: '#64748b' }}>No events detected.</div>
        )}
        {activeEvents.map((event, index) => (
          <div
            key={`${event?.id ?? 'event'}-${index}`}
            style={{
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '8px',
              padding: '6px 10px',
              fontSize: '12px',
              color: '#e2e8f0'
            }}
          >
            <strong>{event?.type ?? 'event'}</strong>
            {event?.faction && <span style={{ color: '#60a5fa' }}> · {event.faction}</span>}
            {event?.intensity && <span style={{ color: '#34d399' }}> · {(event.intensity * 100).toFixed(0)}%</span>}
          </div>
        ))}
      </div>

      {statusMessage && (
        <div style={{ fontSize: '12px', color: '#fca5a5' }}>{statusMessage}</div>
      )}
    </div>
  );
};

export default WorldMapCanvas;
